import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Rating,
  Pagination,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  Avatar,
  Badge,
  Switch,
  Tooltip,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, Link } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import BuildIcon from '@mui/icons-material/Build';
import Nav from '../../Nav';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LaptopIcon from '@mui/icons-material/Laptop';
import TvIcon from '@mui/icons-material/Tv';
import TabletIcon from '@mui/icons-material/Tablet';
import MemoryIcon from '@mui/icons-material/Memory';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import ScreenshotMonitorIcon from '@mui/icons-material/ScreenshotMonitor';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CompareIcon from '@mui/icons-material/Compare';
import MenuBookIcon from '@mui/icons-material/MenuBook';

// Import logo
import logo0 from '../../../assets/logo0.png';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
  }
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  paddingTop: '75%', // 4:3 aspect ratio
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(to bottom, rgba(0,0,0,0) 70%, rgba(0,0,0,0.4) 100%)'
  }
}));

const GreenButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#006938',
  color: 'white',
  '&:hover': {
    backgroundColor: '#004d2b',
  },
  borderRadius: theme.spacing(0.5),
  textTransform: 'none',
  fontWeight: 600
}));

const OutlinedGreenButton = styled(Button)(({ theme }) => ({
  borderColor: '#006938',
  color: '#006938',
  '&:hover': {
    borderColor: '#004d2b',
    backgroundColor: 'rgba(0, 105, 56, 0.04)'
  },
  borderRadius: theme.spacing(0.5),
  textTransform: 'none',
  fontWeight: 600
}));

const CategoryChip = styled(Chip)(({ theme, active }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: active ? '#006938' : 'transparent',
  color: active ? 'white' : '#006938',
  border: active ? 'none' : '1px solid #006938',
  '&:hover': {
    backgroundColor: active ? '#004d2b' : 'rgba(0, 105, 56, 0.1)'
  }
}));

const Parts = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [parts, setParts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [page, setPage] = useState(1);
  const [favorites, setFavorites] = useState([]);
  const [aiRecommendationsEnabled, setAiRecommendationsEnabled] = useState(true);
  const [aiDiagnosticMode, setAiDiagnosticMode] = useState(false);
  const [showAiSuggestions, setShowAiSuggestions] = useState(false);
  
  // Categories with icons
  const categories = [
    { id: 'all', name: 'All Parts', icon: <MemoryIcon /> },
    { id: 'screen', name: 'Screens', icon: <ScreenshotMonitorIcon /> },
    { id: 'battery', name: 'Batteries', icon: <BatteryChargingFullIcon /> },
    { id: 'motherboard', name: 'Motherboards', icon: <MemoryIcon /> },
    { id: 'smartphone', name: 'Smartphone Parts', icon: <PhoneIphoneIcon /> },
    { id: 'laptop', name: 'Laptop Parts', icon: <LaptopIcon /> },
    { id: 'ai-compatible', name: 'AI Compatible', icon: <SmartToyIcon /> }
  ];
  
  // Brands
  const brands = ['all', 'Apple', 'Samsung', 'Dell', 'HP', 'Lenovo', 'Xiaomi', 'LG', 'Sony'];
  
  // Mock data for parts
  const mockParts = [
    {
      id: 0,
      name: 'AI Diagnostic Tool Kit',
      category: 'ai-compatible',
      brand: 'Warrili',
      price: 149.99,
      rating: 4.9,
      reviewCount: 156,
      image: 'https://via.placeholder.com/300x225?text=AI+Diagnostic+Kit',
      compatibility: ['All Devices', 'Warrili AI Platform'],
      inStock: true,
      aiEnhanced: true,
      aiFeatures: ['Auto-Diagnosis', 'Predictive Maintenance', 'Repair Guidance']
    },
    {
      id: 1,
      name: 'iPhone 13 OLED Screen',
      category: 'screen',
      brand: 'Apple',
      price: 129.99,
      rating: 4.5,
      reviewCount: 128,
      image: 'https://via.placeholder.com/300x225?text=iPhone+13+Screen',
      compatibility: ['iPhone 13', 'iPhone 13 Pro'],
      inStock: true
    },
    {
      id: 2,
      name: 'Samsung Galaxy S21 Battery',
      category: 'battery',
      brand: 'Samsung',
      price: 49.99,
      rating: 4.2,
      reviewCount: 95,
      image: 'https://via.placeholder.com/300x225?text=Galaxy+S21+Battery',
      compatibility: ['Samsung Galaxy S21', 'Samsung Galaxy S21+'],
      inStock: true
    },
    {
      id: 3,
      name: 'MacBook Pro A1278 Logic Board',
      category: 'motherboard',
      brand: 'Apple',
      price: 299.99,
      rating: 4.7,
      reviewCount: 42,
      image: 'https://via.placeholder.com/300x225?text=MacBook+Logic+Board',
      compatibility: ['MacBook Pro 13" (2012)', 'MacBook Pro 13" (2011)'],
      inStock: false
    },
    {
      id: 4,
      name: 'Dell XPS 15 Keyboard',
      category: 'laptop',
      brand: 'Dell',
      price: 79.99,
      rating: 4.3,
      reviewCount: 67,
      image: 'https://via.placeholder.com/300x225?text=Dell+XPS+Keyboard',
      compatibility: ['Dell XPS 15 9500', 'Dell XPS 15 9510'],
      inStock: true
    },
    {
      id: 5,
      name: 'iPhone 12 Charging Port',
      category: 'smartphone',
      brand: 'Apple',
      price: 24.99,
      rating: 4.1,
      reviewCount: 103,
      image: 'https://via.placeholder.com/300x225?text=iPhone+Charging+Port',
      compatibility: ['iPhone 12', 'iPhone 12 Pro', 'iPhone 12 Mini'],
      inStock: true
    },
    {
      id: 6,
      name: 'Samsung TV T-Con Board',
      category: 'screen',
      brand: 'Samsung',
      price: 89.99,
      rating: 4.4,
      reviewCount: 36,
      image: 'https://via.placeholder.com/300x225?text=TV+T-Con+Board',
      compatibility: ['Samsung Q60T', 'Samsung Q70T'],
      inStock: true
    },
    {
      id: 7,
      name: 'HP Pavilion Cooling Fan',
      category: 'laptop',
      brand: 'HP',
      price: 34.99,
      rating: 4.0,
      reviewCount: 82,
      image: 'https://via.placeholder.com/300x225?text=HP+Cooling+Fan',
      compatibility: ['HP Pavilion 15', 'HP Pavilion 17'],
      inStock: true
    },
    {
      id: 8,
      name: 'Xiaomi Redmi Note 10 Screen',
      category: 'screen',
      brand: 'Xiaomi',
      price: 59.99,
      rating: 4.2,
      reviewCount: 74,
      image: 'https://via.placeholder.com/300x225?text=Redmi+Screen',
      compatibility: ['Xiaomi Redmi Note 10', 'Xiaomi Redmi Note 10S'],
      inStock: true
    }
  ];
  
  useEffect(() => {
    // Simulate loading data from API
    setTimeout(() => {
      setParts(mockParts);
      setLoading(false);
    }, 1000);
  }, []);
  
  // Toggle favorite
  const toggleFavorite = (partId) => {
    if (favorites.includes(partId)) {
      setFavorites(favorites.filter(id => id !== partId));
    } else {
      setFavorites([...favorites, partId]);
    }
  };
  
  // Handle category change
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setPage(1);
  };
  
  // Handle brand change
  const handleBrandChange = (event) => {
    setSelectedBrand(event.target.value);
    setPage(1);
  };
  
  // Handle sort change
  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };
  
  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };
  
  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Filter and sort parts
  const filteredParts = parts.filter(part => {
    const matchesSearch = part.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || part.category === selectedCategory;
    const matchesBrand = selectedBrand === 'all' || part.brand === selectedBrand;
    
    return matchesSearch && matchesCategory && matchesBrand;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      default: // popular
        return b.reviewCount - a.reviewCount;
    }
  });
  
  // Pagination
  const itemsPerPage = 8;
  const pageCount = Math.ceil(filteredParts.length / itemsPerPage);
  const displayedParts = filteredParts.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  
  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Add Nav component at the top */}
      <Nav />
      <Box sx={{ py: 6 }}>
      <Container maxWidth="xl">
        {/* Enhanced Header with Warrili Branding */}
        <Paper 
          elevation={3} 
          sx={{ 
            mb: 6, 
            p: 4, 
            borderRadius: 2, 
            background: 'linear-gradient(to right, #000000, #222222)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ 
            position: 'absolute', 
            right: { xs: -50, md: 50 }, 
            top: { xs: -50, md: -80 }, 
            width: 200, 
            height: 200, 
            borderRadius: '50%', 
            background: '#FFE302', 
            opacity: 0.2,
            zIndex: 0
          }} />
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
              <img src={logo0} alt="Warrili Logo" style={{ maxWidth: '100%', height: 'auto', maxHeight: '80px' }} />
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography 
                variant="h3" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  color: '#FFE302',
                  mb: 2,
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                AI-Powered Parts Marketplace
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  maxWidth: '800px',
                  mb: 2,
                  textAlign: { xs: 'center', md: 'left' },
                  color: '#ffffff'
                }}
              >
                Find high-quality replacement parts with AI-guaranteed compatibility
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Chip icon={<AutoFixHighIcon />} label="AI Recommendations" sx={{ bgcolor: '#007C3F', color: 'white' }} />
                <Chip icon={<SettingsSuggestIcon />} label="Smart Diagnostics" sx={{ bgcolor: '#00A651', color: 'white' }} />
                <Chip icon={<CompareIcon />} label="Compatibility Check" sx={{ bgcolor: '#FFE302', color: 'black' }} />
              </Box>
            </Grid>
            <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
              <Box sx={{ 
                bgcolor: 'rgba(255,255,255,0.1)', 
                p: 2, 
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <PsychologyIcon sx={{ fontSize: 40, color: '#FFE302', mb: 1 }} />
                <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                  AI Assistant
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<SupportAgentIcon />}
                  onClick={() => setShowAiSuggestions(!showAiSuggestions)}
                  sx={{ 
                    bgcolor: '#FFE302', 
                    color: 'black',
                    '&:hover': { bgcolor: '#d9c000' }
                  }}
                >
                  Get Help
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        {/* AI Suggestions Panel */}
        {showAiSuggestions && (
          <Alert 
            severity="info" 
            icon={<SmartToyIcon />}
            sx={{ 
              mb: 4, 
              borderLeft: '4px solid #FFE302',
              '& .MuiAlert-icon': { color: '#007C3F' }
            }}
            onClose={() => setShowAiSuggestions(false)}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              AI Assistant Suggestions
            </Typography>
            <Typography variant="body2">
              Based on your browsing history, we recommend checking out our new AI Diagnostic Tool Kit and compatible smartphone screens. These parts are most frequently needed for common device issues.
            </Typography>
          </Alert>
        )}
        
        {/* AI Features Toggle */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          p: 2,
          bgcolor: 'rgba(0, 124, 63, 0.05)',
          borderRadius: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SmartToyIcon sx={{ color: '#007C3F', mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              AI Features
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Get AI-powered part recommendations based on your device">
              <FormControl component="fieldset" variant="standard" sx={{ mr: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    Smart Recommendations
                  </Typography>
                  <Switch 
                    checked={aiRecommendationsEnabled}
                    onChange={() => setAiRecommendationsEnabled(!aiRecommendationsEnabled)}
                    color="success"
                  />
                </Box>
              </FormControl>
            </Tooltip>
            <Tooltip title="Enable AI to diagnose your device issues and suggest parts">
              <FormControl component="fieldset" variant="standard">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    Diagnostic Mode
                  </Typography>
                  <Switch 
                    checked={aiDiagnosticMode}
                    onChange={() => setAiDiagnosticMode(!aiDiagnosticMode)}
                    color="success"
                  />
                </Box>
              </FormControl>
            </Tooltip>
          </Box>
        </Box>

        {/* Search and Filters */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search for parts..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 1 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Brand</InputLabel>
                <Select
                  value={selectedBrand}
                  onChange={handleBrandChange}
                  label="Brand"
                >
                  {brands.map((brand) => (
                    <MenuItem key={brand} value={brand}>
                      {brand === 'all' ? 'All Brands' : brand}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={handleSortChange}
                  label="Sort By"
                >
                  <MenuItem value="popular">Most Popular</MenuItem>
                  <MenuItem value="price-low">Price: Low to High</MenuItem>
                  <MenuItem value="price-high">Price: High to Low</MenuItem>
                  <MenuItem value="rating">Highest Rated</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        
        {/* Categories */}
        <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {categories.map((category) => (
            <CategoryChip
              key={category.id}
              label={category.name}
              icon={category.icon}
              active={selectedCategory === category.id}
              onClick={() => handleCategoryChange(category.id)}
              clickable
            />
          ))}
        </Box>
        
        {/* Parts Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#006938' }} />
          </Box>
        ) : displayedParts.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No parts found matching your criteria. Try adjusting your filters.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {displayedParts.map((part) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={part.id}>
                <StyledCard>
                  <Box sx={{ position: 'relative' }}>
                    <StyledCardMedia
                      image={part.image}
                      title={part.name}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1
                      }}
                    >
                      <IconButton
                        onClick={() => toggleFavorite(part.id)}
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.8)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.9)'
                          }
                        }}
                      >
                        {favorites.includes(part.id) ? (
                          <FavoriteIcon sx={{ color: '#e91e63' }} />
                        ) : (
                          <FavoriteBorderIcon />
                        )}
                      </IconButton>
                    </Box>
                    {!part.inStock && (
                      <Chip
                        label="Out of Stock"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          bgcolor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          zIndex: 1
                        }}
                      />
                    )}
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {part.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Rating value={part.rating} precision={0.1} size="small" readOnly />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({part.reviewCount})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {part.compatibility.map((device, index) => (
                        <Chip
                          key={index}
                          label={device}
                          size="small"
                          sx={{ bgcolor: 'rgba(0, 105, 56, 0.1)', color: '#006938' }}
                        />
                      ))}
                    </Box>
                    <Typography variant="h6" color="#006938" fontWeight="bold">
                      ${part.price.toFixed(2)}
                    </Typography>
                    {part.aiEnhanced && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Chip 
                          icon={<SmartToyIcon sx={{ fontSize: '0.8rem' }} />} 
                          label="AI Enhanced" 
                          size="small" 
                          sx={{ 
                            bgcolor: 'rgba(255, 227, 2, 0.2)', 
                            color: '#000000',
                            borderRadius: '4px',
                            height: '24px',
                            '& .MuiChip-label': { px: 1, py: 0 },
                            '& .MuiChip-icon': { color: '#007C3F' }
                          }} 
                        />
                      </Box>
                    )}
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <OutlinedGreenButton
                          size="small"
                          startIcon={<VisibilityIcon />}
                          fullWidth
                          onClick={() => navigate(`/parts/${part.id}`)}
                        >
                          Details
                        </OutlinedGreenButton>
                      </Grid>
                      <Grid item xs={6}>
                        <GreenButton
                          size="small"
                          startIcon={<AddShoppingCartIcon />}
                          fullWidth
                          disabled={!part.inStock}
                        >
                          Add
                        </GreenButton>
                      </Grid>
                    </Grid>
                  </CardActions>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        )}
        
        {/* Pagination */}
        {pageCount > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? 'small' : 'medium'}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#006938'
                },
                '& .Mui-selected': {
                  bgcolor: 'rgba(0, 105, 56, 0.1) !important'
                }
              }}
            />
          </Box>
        )}
        
        {/* Info Sections */}
        <Grid container spacing={4} sx={{ mt: 6 }}>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <MemoryIcon sx={{ fontSize: 60, color: '#006938', mb: 2 }} />
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Genuine Parts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All our parts are either genuine OEM or high-quality aftermarket alternatives that meet or exceed original specifications.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <ShoppingCartIcon sx={{ fontSize: 60, color: '#006938', mb: 2 }} />
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Fast Shipping
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Most orders ship within 24 hours. Express shipping options available for urgent repairs.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <BuildIcon sx={{ fontSize: 60, color: '#006938', mb: 2 }} />
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Installation Support
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get access to detailed installation guides and expert support to help with your repair.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'rgba(255, 227, 2, 0.1)', borderRadius: 2 }}>
              <SmartToyIcon sx={{ fontSize: 60, color: '#007C3F', mb: 2 }} />
              <Typography variant="h6" gutterBottom fontWeight="bold">
                AI-Powered Diagnostics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Our AI technology helps diagnose device issues and recommends the exact parts you need for your repair with 99% accuracy.
              </Typography>
              <Button 
                variant="outlined" 
                startIcon={<MenuBookIcon />}
                size="small"
                sx={{ 
                  mt: 2, 
                  borderColor: '#007C3F', 
                  color: '#007C3F',
                  '&:hover': { borderColor: '#004d2b', bgcolor: 'rgba(0, 124, 63, 0.04)' }
                }}
              >
                Learn More
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
      </Box>
    </Box>
  );
};

export default Parts;